/**
 * Runtime hook for fetching the tenant's inactive-rules map.
 *
 * The Origami API exposes `GET /<module>/component-rules/inactive` which
 * returns a per-tenant map of `component_id -> [ruleId, ...]`. These ids
 * are rules the tenant has locally suppressed without archiving the
 * global rule. The HOC reads from this map to filter out suppressed
 * rules before evaluation.
 *
 * The client and `<module>` come from the nearest
 * `IntegratedComponentProvider` (e.g. `extranetApiClient` +
 * `/extranet/component-rules/inactive` on extranet pages); outside any
 * provider the historical claimflow client/path is used. Claimflow pages
 * mount the provider with the claimflow client, so their request is
 * unchanged. See `./rulesApiTarget.ts`.
 *
 * Fail-open: on any fetch error the hook resolves to an empty map so
 * downstream consumers behave identically to the pre-feature runtime.
 */

import { useQuery } from '@tanstack/react-query';

import { componentRulesPath, useRulesApiTarget } from './rulesApiTarget';

// ---------------------------------------------------------------------------
// Local DTO
// ---------------------------------------------------------------------------

/**
 * Shape of the `GET /<module>/component-rules/inactive` response.
 *
 * `data` is a map of component id → list of rule id strings (uuid).
 */
interface InactiveComponentRulesResponse {
  data: Record<string, string[]>;
}

// ---------------------------------------------------------------------------
// Public hook
// ---------------------------------------------------------------------------

/**
 * Stale-time for the inactive-rules query.
 *
 * Mirrors the 1-minute stale-time used by {@link useComponentRules} so
 * both tenant-wide and per-component rule lookups stay in sync.
 */
const INACTIVE_RULES_STALE_TIME_MS = 60_000;

/**
 * Prefix shared by every module's inactive-rules query key. Invalidating it
 * (as `useUpdateInactiveRules` does) refreshes every module's map.
 */
export const INACTIVE_RULES_QUERY_KEY_PREFIX = [
  'configurable',
  'component-rules',
  'inactive',
] as const;

/**
 * React Query key of the inactive-rules map served by `module`:
 * `['configurable', 'component-rules', 'inactive', module]`.
 */
export function inactiveRulesQueryKey(module: string): readonly string[] {
  return [...INACTIVE_RULES_QUERY_KEY_PREFIX, module];
}

/**
 * Shape returned by {@link useInactiveComponentRules}.
 *
 * `inactiveRuleIdsByComponent` maps component id to a `Set<string>` of
 * suppressed rule ids so the HOC can test membership in O(1). The raw
 * `UseQueryResult` is not re-exported.
 */
export interface UseInactiveComponentRulesResult {
  /** Map of component id → set of inactive rule ids. */
  inactiveRuleIdsByComponent: Record<string, Set<string>>;
  /** `true` while the initial fetch is in flight. */
  isLoading: boolean;
  /** Any error returned by the fetch — `null` / `undefined` on success. */
  error: unknown;
}

/**
 * Fetch the tenant-wide inactive-rules map once per page render tree.
 *
 * Cache key: {@link inactiveRulesQueryKey} of the serving module.
 *
 * Fail-open: any axios error resolves to an empty map; downstream
 * filtering becomes a no-op and components render as they do today.
 */
export function useInactiveComponentRules(): UseInactiveComponentRulesResult {
  const target = useRulesApiTarget();
  const query = useQuery<
    Record<string, string[]>,
    unknown,
    Record<string, Set<string>>
  >({
    queryKey: inactiveRulesQueryKey(target.module),
    queryFn: async () => {
      try {
        const response =
          await target.apiClient.get<InactiveComponentRulesResponse>(
            `${componentRulesPath(target)}/inactive`,
          );
        const map: unknown = response.data.data;
        if (map === null || typeof map !== 'object') return {};
        return map as Record<string, string[]>;
      } catch {
        // Fail-open: return an empty map so downstream consumers
        // behave as if the feature were disabled.
        return {};
      }
    },
    select: (raw): Record<string, Set<string>> => {
      const out: Record<string, Set<string>> = {};
      for (const [componentId, ids] of Object.entries(raw)) {
        if (Array.isArray(ids)) {
          out[componentId] = new Set(ids);
        }
      }
      return out;
    },
    staleTime: INACTIVE_RULES_STALE_TIME_MS,
  });

  return {
    inactiveRuleIdsByComponent: query.data ?? {},
    isLoading: query.isLoading,
    error: query.error,
  };
}
